import { useEffect, useState } from 'react'
import {
  getLiveKitMediaStatus,
  subscribeLiveKitMediaStatus,
  type LiveKitMediaStatus,
} from '../services/liveKitMediaStatusStore'

function isSameStatus(a: LiveKitMediaStatus | null, b: LiveKitMediaStatus | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.micEnabled === b.micEnabled &&
    a.cameraEnabled === b.cameraEnabled &&
    a.cameraWaiting === b.cameraWaiting
  )
}

/**
 * Live mic/camera state for a single LiveKit participant (by `identity`), read
 * directly from the connected Room and refreshed on that participant's LiveKit
 * mute/publish events. Unlike LiveKit's own React hooks it works OUTSIDE the
 * React `RoomContext` (e.g. the Phaser overlay nameplates), via
 * `liveKitMediaStatusStore`.
 *
 * Returns `null` when there is no connected room or the participant is absent,
 * so callers can distinguish "unknown" from a definite unmuted/enabled state.
 */
export function useLiveKitMediaStatus(identity: string): LiveKitMediaStatus | null {
  const [status, setStatus] = useState<LiveKitMediaStatus | null>(() =>
    getLiveKitMediaStatus(identity)
  )

  useEffect(() => {
    const update = () => {
      const next = getLiveKitMediaStatus(identity)
      // Keep the previous object reference when nothing changed to avoid re-renders.
      setStatus((prev) => (isSameStatus(prev, next) ? prev : next))
    }

    // Sync immediately in case state changed between the initial render and effect.
    update()
    return subscribeLiveKitMediaStatus(identity, update)
  }, [identity])

  return status
}
